import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, Trash2, Truck } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  abierto: { label: 'Abierto', cls: 'bg-slate-100 text-slate-700' },
  en_camino: { label: 'En Camino', cls: 'bg-blue-100 text-blue-700' },
  recibido: { label: 'Recibido', cls: 'bg-emerald-100 text-emerald-700' },
};

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const { getAuthHeaders, branches } = useAuth();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchOrder(); }, [id]);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/api/purchase-orders/${id}`, { headers: getAuthHeaders() });
      setOrder(res.data);
    } catch (err) { console.error(err); }
  };

  const branchName = (bid) => branches.find(b => b.id === bid)?.name || bid || '—';

  const updateLine = async (lineId, updates) => {
    // Optimistic UI
    setOrder(prev => prev ? {
      ...prev,
      items: prev.items.map(it => it.line_id === lineId ? { ...it, ...updates } : it),
    } : prev);
    try {
      const res = await axios.patch(`${API}/api/purchase-orders/${id}/lines/${lineId}`, updates, { headers: getAuthHeaders() });
      setOrder(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo actualizar');
      fetchOrder();
    }
  };

  const removeLine = async (lineId) => {
    try {
      await axios.delete(`${API}/api/purchase-orders/${id}/lines/${lineId}`, { headers: getAuthHeaders() });
      fetchOrder();
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const deleteOrder = async () => {
    if (!window.confirm('¿Eliminar el pedido? Todos sus artículos volverán a Artículos por Pedir.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api/purchase-orders/${id}`, { headers: getAuthHeaders() });
      navigate('/purchase-orders');
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo eliminar el pedido');
      setBusy(false);
    }
  };

  const markInTransit = async () => {
    setBusy(true);
    try {
      const res = await axios.patch(`${API}/api/purchase-orders/${id}`, { status: 'en_camino' }, { headers: getAuthHeaders() });
      setOrder(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo actualizar el estado');
    } finally { setBusy(false); }
  };

  if (!order) return <div className="text-slate-400">Cargando…</div>;

  const normStatus = order.status === 'draft' ? 'abierto' : order.status;
  const status = STATUS_LABELS[normStatus] || STATUS_LABELS.abierto;

  return (
    <div data-testid="purchase-order-detail">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Link to="/purchase-orders" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
          <div>
            <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">{order.order_number}</h1>
            <p className="text-xs text-slate-500">
              {new Date(order.created_at).toLocaleString('es-MX', { timeZone: 'America/Hermosillo' })} · {order.created_by}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${status.cls}`}>{status.label}</span>
          {normStatus === 'abierto' && (
            <>
              <Button
                variant="outline"
                onClick={deleteOrder}
                disabled={busy}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="order-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar Pedido
              </Button>
              <Button
                onClick={markInTransit}
                disabled={busy}
                className="bg-[#1D3C6E] hover:bg-[#152B50] text-white"
                data-testid="order-mark-in-transit-btn"
              >
                <Truck className="w-4 h-4 mr-1.5" /> Ya Se Pidió
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Sucursal destino</p>
          <p className="text-sm font-semibold text-slate-800">{branchName(order.branch_id)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Artículos</p>
          <p className="text-sm font-semibold text-slate-800">{order.total_items}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Piezas totales</p>
          <p className="text-sm font-semibold text-slate-800">{order.total_qty}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Costo total</p>
          <p className="text-sm font-semibold text-[#1D3C6E]">${(order.total_cost || 0).toFixed(2)}</p>
        </div>
      </div>

      {order.note && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
          <span className="font-medium">Nota: </span>{order.note}
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Tipo</th>
              <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>SKU</th>
              <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Modelo</th>
              <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider">Nombre</th>
              <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Color</th>
              <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Proveedor</th>
              <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Sucursal</th>
              <th className="text-center px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Cant.</th>
              <th className="text-right px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}></th>
            </tr>
          </thead>
          <tbody>
            {(order.items || []).length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-slate-400"><Package className="w-8 h-8 mx-auto opacity-40" /></td></tr>
            )}
            {(order.items || []).map((line) => (
              <tr key={line.line_id} className="border-b border-slate-100 last:border-b-0" data-testid={`order-line-${line.line_id}`}>
                <td className="px-3 py-2" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${line.custom ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                    {line.custom ? 'Manual' : 'Inventario'}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500" style={{ width: '1%', whiteSpace: 'nowrap' }}>{line.sku || '—'}</td>
                <td className="px-3 py-2 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{line.modelo || '—'}</td>
                <td className="px-3 py-2 font-medium text-slate-800">{line.name}</td>
                <td className="px-3 py-2 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{line.color || '—'}</td>
                <td className="px-3 py-2 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{line.provider || '—'}</td>
                <td className="px-3 py-2" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <Select value={line.branch_id || ''} onValueChange={(v) => updateLine(line.line_id, { branch_id: v })}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`line-branch-${line.line_id}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2 text-center" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <input
                    type="number"
                    min="1"
                    defaultValue={line.cantidad_pedida}
                    onBlur={(e) => {
                      const q = parseInt(e.target.value) || 0;
                      if (q > 0 && q !== line.cantidad_pedida) updateLine(line.line_id, { cantidad_pedida: q });
                    }}
                    className="w-20 h-8 rounded border border-slate-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#AFBD20]/40 focus:border-[#AFBD20]"
                    data-testid={`line-qty-${line.line_id}`}
                  />
                </td>
                <td className="px-3 py-2 text-right" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    onClick={() => removeLine(line.line_id)}
                    className="text-red-400 hover:text-red-600"
                    title="Devolver a Artículos por Pedir"
                    data-testid={`line-remove-${line.line_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
